import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { InternalPage, ResetFormHandler } from 'theSource/components';
import { useApiMutation } from 'theSource/utils';
import { ROUTES } from 'theSource/app/routes';
import { PageTitleStyled } from './MyContactsAddNewPageStyled';
import { ContactUpdateRequestDto, ContactUpdateResponseDto } from '../types';
import {
  ADD_NEW_CONTACT_QUERY_KEY,
  GET_CONTACTS_QUERY_KEY,
  mapFormValuesToContactUpdateRequest,
} from '../utils';
import {
  ADD_EDIT_CONTACT_FORM_INITIAL_VALUES,
  AddEditContactForm,
  AddEditContactFormValues,
} from '../components';
import { BackLink } from 'enrollment/components';

export interface MyContactsAddNewPageProps {
  existingGroups: string[];
}

export const MyContactsAddNewPage = ({
  existingGroups,
}: MyContactsAddNewPageProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation('pages.contacts');

  const { isLoading, mutate: addNewContact } = useApiMutation<
    ContactUpdateResponseDto,
    {},
    ContactUpdateRequestDto
  >({
    queryKey: ADD_NEW_CONTACT_QUERY_KEY,
    queryKeysToInvalidate: GET_CONTACTS_QUERY_KEY,
    invalidateQueriesCallback: () => navigateBackToContacts(),
    method: 'post',
    endpoint: '/contacts?ps_new_ui=true',
    snackbarOptions: {
      position: 'aboveMenu',
    },
  });

  const navigateBackToContacts = () =>
    navigate(ROUTES.additional.profile.contacts.path);

  const handleSubmit = (
    values: AddEditContactFormValues,
    resetForm: ResetFormHandler
  ) =>
    addNewContact(mapFormValuesToContactUpdateRequest(values), {
      onSuccess: () => resetForm(),
    });

  const initialValues: AddEditContactFormValues = {
    ...ADD_EDIT_CONTACT_FORM_INITIAL_VALUES,
    groups: existingGroups.map((group) => ({
      label: group,
      checked: false,
    })),
  };

  return (
    <InternalPage
      title={t('contacts')}
      testId="contacts-add-new-page"
      CustomActionComponent={<BackLink />}
      customActionAlignment="left"
    >
      <PageTitleStyled className="headline-3">
        {t('addContactPageTitle')}
      </PageTitleStyled>
      <AddEditContactForm
        isLoading={isLoading}
        initialValues={initialValues}
        onSubmit={handleSubmit}
        onCancel={navigateBackToContacts}
      />
    </InternalPage>
  );
};
